import React, { useEffect, useState } from "react";
import { Clock, ChefHat, CheckCircle2, Truck, XCircle, Timer, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty, StatusPill } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const NEXT = { pending: "preparing", preparing: "ready", ready: "dispatched", dispatched: "completed" };
const NEXT_LABEL = { pending: "Start Prep", preparing: "Ready", ready: "Dispatch", dispatched: "Complete" };
const NEXT_ICON = { pending: ChefHat, preparing: CheckCircle2, ready: Truck, dispatched: CheckCircle2 };

function fmtTime(t) { return t ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"; }
function mins(a, b) { if (!a || !b) return null; return Math.round((new Date(b) - new Date(a)) / 60000); }

export default function Orders() {
  const { user } = useAuth();
  const canAct = ["admin", "kitchen"].includes(user.role);
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [cancel, setCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [consumed, setConsumed] = useState(false);
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ order_ref: "", channel: "" });

  const saveEdit = async () => {
    try {
      await api.put(`/orders/${edit.id}`, ef);
      toast.success("Order updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const advance = async (o) => {
    try {
      const { data } = await api.patch(`/orders/${o.id}/status`, { status: NEXT[o.status] });
      if (NEXT[o.status] === "completed") toast.success(`Completed — ${money((data.consumption || {}).total_client_charge)} charged to client`);
      else toast.success(`Marked ${NEXT[o.status]}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doCancel = async () => {
    try {
      await api.patch(`/orders/${cancel.id}/status`, { status: "cancelled", reason: cancelReason, ingredients_consumed: cancel.status === "completed" ? consumed : false });
      toast.success("Order cancelled"); setCancel(null); setCancelReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!orders) return <Spinner />;
  const shown = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div>
      <PageHeader title="Orders & KDS" subtitle="Manual order entry with live preparation timestamps" />
      <div className="flex flex-wrap gap-2 mb-5">
        {["all", "pending", "preparing", "ready", "dispatched", "completed", "cancelled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border capitalize ${filter === f ? "border-primary text-amber-300 bg-primary/10" : "border-border text-muted-foreground hover:text-white"}`}>
            {f} {f !== "all" && `(${orders.filter((o) => o.status === f).length})`}
          </button>
        ))}
      </div>

      {shown.length === 0 ? <Empty icon={Clock} title="No orders" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((o) => (
            <div key={o.id} className="card-surface p-4" data-testid={`order-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white truncate">{o.order_ref}</p>
                    {o.over_capacity && <span className="text-[10px] font-bold text-rose-400 border border-rose-500/40 rounded px-1.5 py-0.5">OVER CAP</span>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{o.brand_name} · {o.channel}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {user.role === "admin" && (
                    <button onClick={() => { setEf({ order_ref: o.order_ref, channel: o.channel }); setEdit(o); }} data-testid={`edit-order-${o.id}`} className="text-slate-400 hover:text-amber-300"><Pencil className="h-4 w-4" /></button>
                  )}
                  <StatusPill status={o.status} testid={`order-status-${o.id}`} />
                </div>
              </div>
              <div className="space-y-0.5 mb-3">
                {o.items.map((it, i) => (
                  <div key={`${it.menu_item_id || it.name}-${i}`} className="flex justify-between text-sm">
                    <span className="text-slate-200">{it.name} × {it.qty}</span>
                    <span className="font-mono text-slate-400">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-1 text-[10px] font-mono text-center mb-3">
                {[["Recv", o.received_at], ["Prep", o.prep_started_at], ["Ready", o.ready_at], ["Disp", o.dispatched_at]].map(([l, t]) => (
                  <div key={l} className="rounded bg-secondary/40 py-1">
                    <p className="text-muted-foreground">{l}</p>
                    <p className="text-slate-200">{fmtTime(t)}</p>
                  </div>
                ))}
              </div>
              {o.prep_started_at && o.ready_at && (
                <p className="flex items-center gap-1.5 text-xs text-emerald-400 mb-3"><Timer className="h-3.5 w-3.5" /> Prep {mins(o.prep_started_at, o.ready_at)} min</p>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="font-mono font-bold text-white">{money(o.total_sales)}</span>
                {canAct && o.status !== "completed" && o.status !== "cancelled" && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setCancel(o)} data-testid={`cancel-order-${o.id}`}><XCircle className="h-4 w-4 text-rose-400" /></Button>
                    <Button size="sm" onClick={() => advance(o)} data-testid={`advance-order-${o.id}`} className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold gap-1.5">
                      {NEXT_ICON[o.status] && React.createElement(NEXT_ICON[o.status], { className: "h-4 w-4" })} {NEXT_LABEL[o.status]}
                    </Button>
                  </div>
                )}
              </div>
              {o.consumption && (
                <p className="text-xs text-muted-foreground mt-2 font-mono">Client charge: <span className="text-amber-300">{money(o.consumption.total_client_charge)}</span></p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>Edit Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">Order reference</label><Input value={ef.order_ref} onChange={(e) => setEf({ ...ef, order_ref: e.target.value })} data-testid="edit-order-ref" className="mt-1" /></div>
            <div><label className="text-xs text-muted-foreground">Channel</label><Input value={ef.channel} onChange={(e) => setEf({ ...ef, channel: e.target.value })} data-testid="edit-order-channel" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-order-edit" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>Cancel Order</DialogTitle></DialogHeader>
          <Input placeholder="Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason" />
          {cancel?.status === "completed" && (
            <label className="flex items-center gap-2 text-sm text-slate-300 mt-2">
              <input type="checkbox" checked={consumed} onChange={(e) => setConsumed(e.target.checked)} /> Ingredients were actually consumed
            </label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancel(null)}>Keep</Button>
            <Button onClick={doCancel} data-testid="confirm-cancel" className="bg-rose-600 hover:bg-rose-500 text-white">Cancel Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
